import * as EventTracker from '@src/analytics/EventTracker';
import { SavePullQuoteService } from '@src/services/transcript/SavePullQuoteService';
import {
  useContentStudioUI,
  useDatoClient,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import CircleCheckIcon from '../../../svgs/CircleCheckIcon';
import GridViewIcon from '../../../svgs/GridViewIcon';
import ListViewIcon from '../../../svgs/ListViewIcon';
import Modal from '../../common/Modal';
import SkeletonBox from '../../common/SkeletonBox';
import PullQuoteLimitDropdown from './PullQuoteLimitDropdown';
import { CARD_HEADER_ROW_HEIGHT } from './PullQuoteVisualCard';
import PullQuoteSelectionCard from './PullQuoteSelectionCard';
import { PullQuoteSelectionSummaryBar } from './PullQuoteSelectionSummaryBar';
import PullQuoteSortDropdown from './PullQuoteSortDropdown';
import PullQuoteVisualCard from './PullQuoteVisualCard';
import { usePullQuoteSelection } from './usePullQuoteSelection';

import type { ShareableImageType } from '../../../types.ts/story';
import type { CompositionTemplate } from '../../sidepanel/CompositionSettings/types';
import type { PullQuoteGenerationContext } from './PullQuoteSelectionSummaryBar';
import type { PullQuoteSortMode } from './usePullQuoteSelection';

const logger = LogManager.getLogger('PullQuoteResultsModal');

type ViewMode = 'text' | 'visual';

type Props = {
  quotes: ShareableImageType[];
  isLoading: boolean;
  onClose: () => void;
  compositionTemplates: CompositionTemplate[];
  generationContext?: PullQuoteGenerationContext;
};

const PullQuoteResultsModal = observer((props: Props) => {
  const {
    quotes,
    isLoading,
    onClose,
    compositionTemplates,
    generationContext,
  } = props;
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const userIdentity = useUserIdentity();
  const contentStudioUI = useContentStudioUI();
  const [viewMode, setViewMode] = useState<ViewMode>('visual');

  const pullQuoteService = useMemo(
    () => new SavePullQuoteService({ datoClientStore }),
    [datoClientStore],
  );

  const {
    sortedQuotes,
    sortMode,
    setSortMode,
    quoteLimit,
    setQuoteLimit,
    totalQuotes,
  } = usePullQuoteSelection(quotes);

  // Each visual card is a renderer iframe, and a browser reloads an iframe that
  // moves in the DOM — the renderer does not re-initialize after that, so the
  // card goes blank. Cards therefore stay in arrival order in the DOM and take
  // their sorted position from CSS `order`; re-sorting (a score landing
  // mid-stream, or the sort dropdown) never moves a node.
  const rankById = useMemo(
    () => new Map((sortedQuotes ?? []).map((q, i) => [q.id, i])),
    [sortedQuotes],
  );

  const [quotesSelected, setQuotesSelected] = useState<Set<string>>(new Set());

  const handleSelectQuote = useCallback((quoteId: string) => {
    setQuotesSelected(prev => {
      const next = new Set(prev);
      if (next.has(quoteId)) {
        next.delete(quoteId);
      } else {
        next.add(quoteId);
      }
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (quotesSelected.size === 0) return;
    const { story } = videoCreator;
    if (!story) return;

    const selectedQuotes = quotes.filter(q => quotesSelected.has(q.id));

    // Close immediately so the save runs against the Content Studio page the
    // user is waiting to see. A full-screen loader here only hid the quotes
    // for the length of the (sequential, per-quote) Dato writes; progress
    // lives in a pending toast instead.
    onClose();

    const savingToastId = videoCreator.showToast({
      state: 'pending',
      message: `Saving ${selectedQuotes.length} pull quote${selectedQuotes.length !== 1 ? 's' : ''}…`,
    });

    analytics.track('pull_quotes_batch_saved', {
      storyId: story.id,
      storyTitle: story.title,
      quotesCount: selectedQuotes.length,
    });

    const compositionId = pullQuoteService.resolveDefaultCompositionId(
      compositionTemplates,
      videoCreator.organization?.defaultPullQuoteTemplate?.id,
    );

    const result = await pullQuoteService.savePullQuoteBatch({
      story,
      quotes: selectedQuotes,
      compositionId,
      editorName: userIdentity.currentEditor,
    });

    if (!result.ok) {
      logger.error('Batch save failed entirely:', result.error);
      videoCreator.updateToast(savingToastId, {
        state: 'error',
        message: 'Error occurred while saving pull quotes',
        delay: 3000,
      });
      return;
    }

    const { savedQuotes, failedCount } = result.value;

    // Track analytics for each saved quote
    for (const saved of savedQuotes) {
      EventTracker.trackPullQuoteSaved({
        story_id: story.id,
        card_id: saved.id,
        has_image: !!saved.imagefile?.url,
      });
    }

    // Update local state with whatever succeeded. The modal is already closed,
    // so the user may have moved to another story while the writes ran — stay
    // on the captured story and only touch store state still scoped to it.
    if (savedQuotes.length > 0) {
      const existingSaved = story.shareableImages || [];
      story.shareableImages = [
        ...savedQuotes,
        ...existingSaved,
      ] as ShareableImageType[];

      try {
        await videoCreator.updateStory(story);
      } catch (error) {
        // Only throwing call here, and the pending toast is now the sole
        // progress surface — leaving it unresolved spins forever.
        logger.error(
          'Failed to attach saved pull quotes to story',
          extractErrorInfo(error),
        );
        videoCreator.updateToast(savingToastId, {
          state: 'error',
          message: 'Error occurred while saving pull quotes',
          delay: 3000,
        });
        return;
      }

      // Track saved IDs for highlight animation on the main page
      const savedIds = savedQuotes.map(q => q.id).filter(Boolean) as string[];
      if (savedIds.length > 0) {
        contentStudioUI.markQuotesSaved(savedIds);
      }
    }

    if (failedCount > 0 && savedQuotes.length > 0) {
      videoCreator.updateToast(savingToastId, {
        state: 'warning',
        message: `${savedQuotes.length} pull quote${savedQuotes.length !== 1 ? 's' : ''} saved, ${failedCount} failed`,
        delay: 3000,
      });
    } else if (failedCount > 0) {
      videoCreator.updateToast(savingToastId, {
        state: 'error',
        message: `All ${failedCount} pull quote${failedCount !== 1 ? 's' : ''} failed to save`,
        delay: 3000,
      });
    } else {
      videoCreator.updateToast(savingToastId, {
        state: 'success',
        message: `${savedQuotes.length} pull quote${savedQuotes.length !== 1 ? 's' : ''} saved successfully`,
        delay: 3000,
      });
    }
  };

  return (
    <Modal
      isOpen
      closeModal={onClose}
      contentStyle={{
        marginTop: '70px',
        marginBottom: '70px',
      }}
    >
      <Wrapper data-testid="pull-quote-results-modal">
        <Title>Select your pull quotes to save</Title>
        <Heading>
          {isLoading && totalQuotes === 0
            ? 'Generating pull quotes…'
            : sortedQuotes.length === totalQuotes
              ? `${totalQuotes} quotes generated`
              : `Showing ${sortedQuotes.length} of ${totalQuotes} quotes`}
        </Heading>
        <PullQuoteSelectionSummaryBar
          quotes={quotes}
          quotesSelected={quotesSelected}
          generationContext={generationContext}
        />
        <TopBar>
          <DropdownsRow>
            <PullQuoteSortDropdown
              sortMode={sortMode as PullQuoteSortMode}
              onSortChange={setSortMode}
            />
            <PullQuoteLimitDropdown
              quoteLimit={quoteLimit}
              onLimitChange={setQuoteLimit}
            />
            <ViewToggleGroup>
              <ViewToggleButton
                $active={viewMode === 'visual'}
                onClick={() => setViewMode('visual')}
                title="Visual preview"
              >
                <GridViewIcon
                  width="14"
                  height="14"
                  strokeColor={
                    viewMode === 'visual'
                      ? 'var(--accent)'
                      : 'var(--text-muted)'
                  }
                />
              </ViewToggleButton>
              <ViewToggleButton
                $active={viewMode === 'text'}
                onClick={() => setViewMode('text')}
                title="Text view"
              >
                <ListViewIcon
                  width="14"
                  height="14"
                  strokeColor={
                    viewMode === 'text' ? 'var(--accent)' : 'var(--text-muted)'
                  }
                />
              </ViewToggleButton>
            </ViewToggleGroup>
          </DropdownsRow>
          <SaveButton
            disabled={quotesSelected.size === 0}
            onClick={handleSave}
            data-testid="save-pull-quotes-button"
          >
            <CircleCheckIcon
              strokeColor="currentColor"
              width="18px"
              height="18px"
            />
            <span>Save as Pull Quotes</span>
          </SaveButton>
        </TopBar>
        <ScrollArea>
          <QuotesGrid>
            {quotes
              .filter(quote => rankById.has(quote.id))
              .map(quote => (
                <div key={quote.id} style={{ order: rankById.get(quote.id) }}>
                  {viewMode === 'visual' ? (
                    <PullQuoteVisualCard
                      quote={quote}
                      isSelected={quotesSelected.has(quote.id)}
                      onSelect={handleSelectQuote}
                      compositionTemplates={compositionTemplates}
                    />
                  ) : (
                    <PullQuoteSelectionCard
                      quote={quote}
                      isSelected={quotesSelected.has(quote.id)}
                      onSelect={handleSelectQuote}
                    />
                  )}
                </div>
              ))}
            {/* Skeleton of the card that is coming: a full row of them while
                nothing is back yet, one trailing card once quotes stream in.
                The heading above carries the "Generating pull quotes…" copy. */}
            {isLoading &&
              Array.from(
                { length: totalQuotes === 0 ? SKELETON_CARDS : 1 },
                (_, i) => (
                  <SkeletonCard
                    key={`skeleton-${i}`}
                    aria-hidden
                    style={{ order: totalQuotes + i }}
                  >
                    <SkeletonCardHeader>
                      <SkeletonBox $width="44px" $height="20px" />
                      <SkeletonBox $width="20px" $height="20px" $radius="50%" />
                    </SkeletonCardHeader>
                    <SkeletonCanvas />
                  </SkeletonCard>
                ),
              )}
          </QuotesGrid>
          {!isLoading && quotes.length === 0 && (
            <EmptyState>
              No quotes were generated. Try again with different settings.
            </EmptyState>
          )}
        </ScrollArea>
      </Wrapper>
    </Modal>
  );
});

export default PullQuoteResultsModal;

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--background);
  width: 942px;
  max-width: calc(100vw - 80px);
  border: 1px solid var(--border);
  height: calc(100vh - 140px);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 40px 24px 24px;
  color: var(--primary-hover);
`;

const Title = styled.div`
  width: 100%;
  color: var(--foreground);
  text-align: center;
  font-size: 20px;
  font-weight: var(--font-weight-bold);
  line-height: normal;
`;

const Heading = styled.div`
  width: 100%;
  text-align: center;
  color: var(--primary-hover);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 150%;
  text-transform: uppercase;
`;

const TopBar = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DropdownsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ViewToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 6px;
  border: 1px solid var(--border);
`;

const ViewToggleButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: none;
  padding: 0;
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:active {
    scale: 0.95;
  }
`;

const SaveButton = styled.button<{ disabled: boolean }>`
  cursor: pointer;
  display: flex;
  position: relative;
  padding: 11px 16px 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--primary-hover);
  background-color: var(--primary-hover);
  color: var(--background);

  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: normal;
  ${props =>
    props.disabled &&
    `
    background-color: var(--background);
    color: var(--border);
    border: 1px solid var(--border);
    cursor: not-allowed;
  `}
`;

const ScrollArea = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  min-height: 0;
  padding: 0 2px;
  mask-image: linear-gradient(
    to bottom,
    transparent 0px,
    black 16px,
    black calc(100% - 16px),
    transparent 100%
  );
`;

const QuotesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 20px;
  padding-bottom: 8px;
`;

/* PullQuoteVisualCard's shell, header row and 1:1 canvas, in SkeletonBox. */
const SKELETON_CARDS = 6;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1.5px solid var(--border);
  background-color: var(--surface);
  box-sizing: border-box;
  overflow: hidden;
`;

const SkeletonCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 0;
  min-height: ${CARD_HEADER_ROW_HEIGHT};
`;

const SkeletonCanvas = styled(SkeletonBox)`
  height: auto;
  border-radius: 0;
  aspect-ratio: 1 / 1;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--text-muted);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
`;
